import React from 'react';
import * as i18n from 'i18next';
import { reduxForm } from 'redux-form';
import { Button, Col, Container } from 'reactstrap';
import { FORMS, RELATIVE_PAGE_PATHS } from '../../utils/constants';
import { formUtils } from '../../utils/formUtils';
import { connect } from 'react-redux';
import { Fields } from '../common/Fields';
import navigationMethods from '../../utils/navigationUtils';
import {
  showDeleteModal,
  closeModal,
  showInfoModal,
  showConfirmModal,
  showRedConfirmModal,
  showErrorModal,
  showSuccessModal,
  setJournalTokenActiveStatusToFalse,
  setJournalTokenActiveStatusToTrue,
} from '../../actions';

import {
  addContact,
  getContact,
  getContacts,
  updateContact,
  deleteContact,
  handleStopSharingJournalContact,
  shareJournalThroughEmailContact,
  handleShareJournalClickContact,
} from '../../actions/contactActions';
import { getAxiosWithToken } from '../../utils/webApi';

class ContactsDetailScreen extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isJournalShared: null,
      emailMessageShown: false,
      loading: true,
      hasSharedAnalyticsLink: false,
      sharedAnalyticsLinkUrl: '',
      isSharingAnalytics: false,
      isCopyingAnalyticsLink: false,
      isStoppingAnalyticsShare: false,
    };
  }

  async componentDidMount() {
    const id = this.props.match.params.id;
    if (id) {
      if (!this.props.contacts.length) {
        await this.props.getContacts();
      }
      await this.props.getContact(id);
      this.setState({
        isJournalShared: this.props.currentContact.isJournalShared,
        loading: false,
      });
    } else {
      this.setState({ loading: false });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.currentContact !== prevProps.currentContact) {
      this.setState({
        isJournalShared: this.props.currentContact.isJournalShared,
      });
    }
  }

  handleSubmit = (formValues) => {
    const id = this.props.match.params.id;
    if (id) {
      this.props.updateContact(id, formValues);
    } else {
      this.props.addContact(formValues);
    }
  };

  onDelete = () => {
    const id = this.props.match.params.id;
    const message = i18n.t('contactsScreen:confirmDelete');
    this.props.showDeleteModal({
      message,
      onDelete: () => {
        this.props.deleteContact(id);
        this.props.closeModal();
      },
    });
  };

  normalizeEncryptedUserId = (value) => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };

  buildAnalyticsShareUrl = (encryptedUserId, token) => {
    const normalizedEncryptedUserId =
      this.normalizeEncryptedUserId(encryptedUserId);

    return (
      `${window.location.origin}` +
      `${RELATIVE_PAGE_PATHS.ANALYTICS_READ_ONLY}` +
      `?user-id=${encodeURIComponent(normalizedEncryptedUserId)}` +
      `&token=${encodeURIComponent(token)}` +
      `&type=${encodeURIComponent('growth')}`
    );
  };

  handleShareAnalytics = async () => {
    try {
      this.setState({ isSharingAnalytics: true });
      const response = await getAxiosWithToken().post(
        'trackers/shared-analytics-link'
      );

      const returnedData = response.data;
      if (!returnedData?.success) {
        this.props.showErrorModal(i18n.t('analytics:shared.shareFailed'));
        return;
      }

      const encryptedUserId = returnedData.data?.encryptedUserId;
      const token = returnedData.data?.token;

      if (!encryptedUserId || !token) {
        this.props.showErrorModal(i18n.t('analytics:shared.shareFailed'));
        return;
      }

      const shareUrl = this.buildAnalyticsShareUrl(encryptedUserId, token);
      this.setState({
        hasSharedAnalyticsLink: true,
        sharedAnalyticsLinkUrl: shareUrl,
      });

      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        window.prompt(i18n.t('analytics:shared.copyThisLink'), shareUrl);
        return;
      }

      this.props.showSuccessModal(i18n.t('analytics:shared.shareCopied'));
    } catch (error) {
      console.error('Error generating share analytics link:', error);
      this.props.showErrorModal(i18n.t('analytics:shared.shareFailed'));
    } finally {
      this.setState({ isSharingAnalytics: false });
    }
  };

  handleCopySharedAnalyticsLink = async () => {
    const { sharedAnalyticsLinkUrl } = this.state;

    if (!sharedAnalyticsLinkUrl) {
      this.props.showErrorModal(i18n.t('analytics:shared.shareFailed'));
      return;
    }

    try {
      this.setState({ isCopyingAnalyticsLink: true });

      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(sharedAnalyticsLinkUrl);
      } else {
        window.prompt(
          i18n.t('analytics:shared.copyThisLink'),
          sharedAnalyticsLinkUrl
        );
        return;
      }

      this.props.showSuccessModal(i18n.t('analytics:shared.shareCopied'));
    } catch (error) {
      console.error('Error copying share analytics link:', error);
      this.props.showErrorModal(i18n.t('analytics:shared.shareFailed'));
    } finally {
      this.setState({ isCopyingAnalyticsLink: false });
    }
  };

  handleStopSharingAnalytics = async () => {
    try {
      this.setState({ isStoppingAnalyticsShare: true });
      const response = await getAxiosWithToken().patch(
        'trackers/shared-analytics-link/disable'
      );

      const returnedData = response.data;
      if (!returnedData?.success) {
        this.props.showErrorModal(
          'Unable to disable shared analytics links. Please try again.'
        );
        return;
      }

      const disabledCount =
        returnedData?.data?.disabledLinksCount ??
        returnedData?.data?.DisabledLinksCount ??
        0;

      if (!disabledCount) {
        this.props.showErrorModal(
          'No active shared analytics links were found to disable.'
        );
        return;
      }

      this.props.showSuccessModal(
        'Shared analytics links are now disabled and no longer accessible.'
      );
      this.setState({
        hasSharedAnalyticsLink: false,
        sharedAnalyticsLinkUrl: '',
      });
    } catch (error) {
      console.error('Error disabling shared analytics links:', error);
      this.props.showErrorModal(
        'Unable to disable shared analytics links. Please try again.'
      );
    } finally {
      this.setState({ isStoppingAnalyticsShare: false });
    }
  };

  renderDeleteButton = () => {
    if (this.props.match.params.id) {
      return (
        <Button
          onClick={this.onDelete}
          className="greenBtn header next uppercase secondary danger mt-3"
        >
          {i18n.t('buttons:delete')}
        </Button>
      );
    }
    return null;
  };

  renderShareButton = () => {
    const { currentContact } = this.props;
    const id = this.props.match.params.id;
    const isJournalShared = this.state.isJournalShared;
    const shareButton = i18n.t('contactsScreen:buttons:shareJournal');
    const stopSharingButton = i18n.t('contactsScreen:buttons:stopsharingJournal');

    if (this.props.match.params.id) {
      if (!currentContact.email && !this.state.emailMessageShown) {
        this.props.showInfoModal(i18n.t('contactsScreen:addEmail'));
        this.setState({ emailMessageShown: true });
        return null;
      }

      const buttonText = isJournalShared ? stopSharingButton : shareButton;
      const buttonClass = isJournalShared
        ? 'redBtn header next uppercase mt-3'
        : 'greenBtn header next uppercase mt-3';

      if (id && currentContact.email) {
        return (
          <Button
            onClick={() => {
              const message = isJournalShared
                ? i18n.t('contactsScreen:confirmStopSharingJournal')
                : i18n.t('contactsScreen:confirmShareJournal');
              if (!isJournalShared) {
                this.props.showConfirmModal({
                  message,
                  onConfirm: () => {
                    this.props.shareJournalThroughEmailContact(currentContact.email);
                    this.props.handleShareJournalClickContact(this.props.match.params.id);
                    this.setState({ isJournalShared: true });
                  },
                });
              } else {
                this.props.showRedConfirmModal({
                  message,
                  onConfirm: () => {
                    this.props.setJournalTokenActiveStatusToFalse(
                      this.props.currentContact.email
                    );
                    this.props.handleStopSharingJournalContact(this.props.match.params.id);
                    this.setState({ isJournalShared: false });
                  },
                });
              }
            }}
            className={buttonClass}
          >
            {buttonText}
          </Button>
        );
      }
      return null;
    }
    return null;
  };

  renderShareAnalyticsButtons = () => {
    const { currentContact } = this.props;
    const id = this.props.match.params.id;
    const {
      hasSharedAnalyticsLink,
      isSharingAnalytics,
      isCopyingAnalyticsLink,
      isStoppingAnalyticsShare,
    } = this.state;

    if (!id || !currentContact.email) {
      return null;
    }

    const isDisabled =
      isSharingAnalytics || isCopyingAnalyticsLink || isStoppingAnalyticsShare;

    if (!hasSharedAnalyticsLink) {
      return (
        <Button
          onClick={this.handleShareAnalytics}
          className="greenBtn header next uppercase mt-3"
          disabled={isDisabled}
        >
          {i18n.t('analytics:shared.share')}
        </Button>
      );
    }

    return (
      <>
        <Button
          onClick={this.handleCopySharedAnalyticsLink}
          className="greenBtn header next uppercase mt-3"
          disabled={isDisabled}
        >
          {i18n.t('analytics:shared.copyThisLink')}
        </Button>
        <Button
          onClick={this.handleStopSharingAnalytics}
          className="redBtn header next uppercase mt-3"
          disabled={isDisabled}
        >
          {i18n.t('analytics:shared.stopSharing')}
        </Button>
      </>
    );
  };

  render() {
    const id = parseInt(this.props.match.params.id);
    const contactNumber =
      this.props.contacts.findIndex((contact) => contact && contact.id === id) + 1;

    const isNewContact =
      navigationMethods.getRoute() === RELATIVE_PAGE_PATHS.ADD_CONTACT;
    const title = isNewContact
      ? i18n.t('contactsScreen:buttons:addContact')
      : i18n.t('contactsScreen:contact', {
        word1: contactNumber,
      });
    const buttonName = isNewContact
      ? i18n.t('contactsScreen:buttons.add')
      : i18n.t('buttons:saveChanges');

    if (this.state.loading) {
      return <div></div>;
    }

    return (
      <Container className="noPadding">
        <Col xs={12} sm={12} md={4} lg={4} className="centred noPadding">
          <form
            onSubmit={this.props.handleSubmit(this.handleSubmit)}
            className="flexContainerColumn flexContainerCentered profile-form fix-padding"
          >
            <div className="flexContainerCentered">
              <p className="header mainGreenColor header-small uppercase">
                {title}
              </p>
            </div>

            <div className="flexContainerColumn flexContainerCentered mt-3">
              {Fields.contacts.name()}
              <div className="mt-4">
                {Fields.commonFields.email()}
                {Fields.contacts.phone()}
                {Fields.contacts.address({
                  className: 'contacts-address input-container',
                  rows: 10,
                })}
              </div>
            </div>

            <Col
              xs={10}
              sm={10}
              md={10}
              lg={8}
              className="flexContainerColumn centred mt-5 mb-5"
            >
              <Button
                type="submit"
                disabled={this.props.submitting}
                className="greenBtn header next uppercase"
              >
                {buttonName}
              </Button>

              {/* Share Journal */}
              {this.renderShareButton()}

              {/* Share Analytics - directly under Share Journal */}
              {this.renderShareAnalyticsButtons()}

              {this.renderDeleteButton()}
            </Col>
          </form>
        </Col>
      </Container>
    );
  }
}

const contactForm = reduxForm({
  form: FORMS.SPECIALIST_FORM,
  validate: formUtils.validateContacts,
  destroyOnUnmount: true,
  enableReinitialize: true,
})(ContactsDetailScreen);

const mapStateToProps = (state, props) => {
  const id = props.match.params.id;
  return {
    initialValues: id ? state.contacts.contact : {},
    contacts: state.contacts.contacts,
    currentContact: id ? state.contacts.contact : {},
  };
};

export default connect(mapStateToProps, {
  setJournalTokenActiveStatusToTrue,
  setJournalTokenActiveStatusToFalse,
  handleStopSharingJournalContact,
  handleShareJournalClickContact,
  addContact,
  getContact,
  getContacts,
  updateContact,
  deleteContact,
  showDeleteModal,
  closeModal,
  showInfoModal,
  showErrorModal,
  showSuccessModal,
  shareJournalThroughEmailContact,
  showConfirmModal,
  showRedConfirmModal,
})(contactForm);
